import { useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";

const secondaryBtnStyle = {
  width: "max-content",
  padding: "0.5rem 2rem",
  boxShadow: "none",
  borderRadius: "8px",
  color: "#194AF5",
  bgcolor: "#E6EBFF",
  ":hover": { bgcolor: "#E6EBFF", color: "#194AF5", boxShadow: "none" },
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const serverMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const RowList = ({ label, rows, color }) => (
  <>
    <Box sx={{ fontWeight: 600, mb: 1, mt: 1 }}>{label}</Box>
    <Box
      component="ul"
      aria-label={label}
      sx={{ listStyle: "none", p: 0, m: 0, maxHeight: 240, overflowY: "auto" }}
    >
      {rows.map((item, index) => (
        <Box
          component="li"
          key={`${item.row}-${item.name}-${index}`}
          sx={{ py: 1, borderBottom: "1px solid #EEE", fontSize: "0.875rem" }}
        >
          <Box sx={{ fontWeight: 600 }}>
            {item.row ? `Row ${item.row}` : "Row"}
            {item.name ? ` — ${item.name}` : ""}
          </Box>
          <Box sx={{ color }}>Reason: {item.reason}</Box>
        </Box>
      ))}
    </Box>
  </>
);

const MasterSheetActions = ({
  api,
  files,
  exportParams = {},
  onImported,
  disabled = false,
  inputTestId,
  importSummary,
  importFailures,
  importSkipped = () => [],
}) => {
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleDownload = async (kind) => {
    setBusy(true);
    try {
      const response = await api.downloadFile(kind, kind === "export" ? exportParams : {});
      saveBlob(response.data, kind === "export" ? files.export : files.template);
    } catch (err) {
      toast.error(serverMessage(err, "Download failed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = null;
    if (!file) return;

    setBusy(true);
    try {
      const response = await api.importFile(file);
      const summary = importSummary(response?.data);
      const failures = importFailures(response?.data);
      const skipped = importSkipped(response?.data);

      // Refused and skipped rows need their reasons, which a toast cannot hold.
      if (failures.length > 0 || skipped.length > 0) {
        setImportResult({ message: summary.message, failures, skipped });
      } else if (summary.level === "success") {
        toast.success(summary.message);
      } else {
        toast.info(summary.message);
      }
      onImported?.();
    } catch (err) {
      toast.error(serverMessage(err, "Import failed."));
    } finally {
      setBusy(false);
    }
  };

  const isBusy = busy || disabled;

  return (
    <>
      <Button
        sx={secondaryBtnStyle}
        variant="contained"
        disabled={isBusy}
        onClick={() => handleDownload("template")}
      >
        Download Template
      </Button>

      <Button
        sx={secondaryBtnStyle}
        variant="contained"
        disabled={isBusy}
        onClick={() => fileInputRef.current?.click()}
      >
        Import
      </Button>
      <input
        type="file"
        accept=".xlsx,.xls,.csv"
        ref={fileInputRef}
        hidden
        data-testid={inputTestId}
        onChange={handleImport}
      />

      <Button
        sx={secondaryBtnStyle}
        variant="contained"
        disabled={isBusy}
        onClick={() => handleDownload("export")}
      >
        Export
      </Button>

      <Dialog
        open={Boolean(importResult)}
        onClose={() => setImportResult(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Import complete</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 1 }}>{importResult?.message}</DialogContentText>
          {importResult?.failures?.length > 0 && (
            <RowList label="Failed rows" rows={importResult.failures} color="#d32f2f" />
          )}
          {importResult?.skipped?.length > 0 && (
            <RowList label="Skipped rows" rows={importResult.skipped} color="#8a6d3b" />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MasterSheetActions;
